import { ref, computed, watch } from 'vue'
import { api } from '@/core/api'
import { logger } from '@/utils/logger'
import { useSettingsStore, type ProviderMeta, type AiModelInfo } from '@/stores/settings'
import { useProjectsStore } from '@/stores/projects'

let lastLoadedProjectId: string | null = null

/** Providers still supported by the API but hidden from the model picker UI. */
const HIDDEN_UI_PROVIDERS = new Set(['deepseek', 'grok', 'qwen'])

/** Keep in sync with backend DEFAULT_SMART_MODEL_ID in AISettings entity. */
const DEFAULT_SMART_MODEL_ID = 'claude-sonnet-4-6'

/**
 * Shared project-scoped AI model catalog + persistence (PUT project ai-settings).
 * Used by header AiModelSelector and Coddy compact selects. Module-level guard avoids duplicate loads per project.
 */
export function useProjectAiModelPicker() {
  const settingsStore = useSettingsStore()
  const projectsStore = useProjectsStore()

  const projectId = computed(() => projectsStore.currentProject?.id ?? null)
  const loading = ref(false)
  const loadFailed = ref(false)

  const allModels = computed(() => {
    const result: AiModelInfo[] = []
    for (const models of Object.values(settingsStore.modelsByProvider)) {
      result.push(...models)
    }
    return result
  })

  const selectedModel = computed(() =>
    allModels.value.find(m => m.id === settingsStore.selectedModel),
  )

  const orderedProviders = computed(() =>
    settingsStore.providerMeta
      .map(meta => ({
        meta,
        models: settingsStore.modelsByProvider[meta.type] || [],
      }))
      .filter(p => p.models.length > 0),
  )

  function isModelSelectable(model: AiModelInfo): boolean {
    if (!model.requiresApiKey) return true
    return settingsStore.hasApiKey(model.provider)
  }

  async function loadModels(): Promise<void> {
    const pid = projectId.value
    if (!pid) return

    loading.value = true
    loadFailed.value = false
    try {
      const [modelsRes, projectSettingsRes, aiSettingsRes] = await Promise.all([
        api.get<{
          claude: AiModelInfo[]
          providers: Array<{
            type: string
            displayName: string
            requiresApiKey: boolean
            apiKeyPlaceholder: string
            helpUrl: string
          }>
          models: Record<string, AiModelInfo[]>
        }>('/ai/models'),
        api.request<{
          smartModelId?: string | null
          smartModelProvider?: string | null
        }>('GET', `/projects/${pid}/ai-settings`).catch(() => null),
        api.getAISettings().catch(() => null),
      ])

      const rawProviders = (modelsRes?.providers || []).filter(
        p => !HIDDEN_UI_PROVIDERS.has(p.type),
      )
      const providers: ProviderMeta[] = rawProviders.map(p => ({
        type: p.type,
        displayName: p.displayName,
        requiresApiKey: p.requiresApiKey,
        apiKeyPlaceholder: p.apiKeyPlaceholder,
        apiKeyHelpUrl: p.helpUrl,
      }))
      settingsStore.setProviderMeta(providers)

      const modelMap: Record<string, AiModelInfo[]> = {}
      const rawModels = modelsRes?.models || {}
      for (const [providerType, models] of Object.entries(rawModels)) {
        if (HIDDEN_UI_PROVIDERS.has(providerType)) continue
        modelMap[providerType] = (models || []).map(m => ({
          id: m.id,
          name: m.name,
          description: m.description,
          category: m.category,
          provider: m.provider || providerType,
          requiresApiKey: m.requiresApiKey ?? false,
        }))
      }
      settingsStore.setModelsByProvider(modelMap)

      const flatIds = Object.values(modelMap).flat().map(m => m.id)
      settingsStore.setAvailableModels(flatIds)

      if (flatIds.length === 0) {
        loadFailed.value = true
        return
      }

      if (aiSettingsRes) {
        const settings = aiSettingsRes as Record<string, unknown>
        const savedKeys = settings.customApiKeys as Record<string, string> | null
        if (savedKeys) {
          for (const [prov, key] of Object.entries(savedKeys)) {
            if (key) {
              settingsStore.setCustomApiKey(prov, key)
            }
          }
        }
      }

      const savedModel = projectSettingsRes?.smartModelId
      const savedProvider = projectSettingsRes?.smartModelProvider
      if (savedModel && flatIds.includes(savedModel)) {
        settingsStore.setSelectedModel(savedModel, savedProvider || undefined)
      } else {
        const claudeModels = modelMap['claude'] || []
        let fallback: string
        if (flatIds.includes(DEFAULT_SMART_MODEL_ID)) {
          fallback = DEFAULT_SMART_MODEL_ID
        } else {
          const sonnet = claudeModels.find(m => m.category === 'smart')
          fallback = sonnet?.id || claudeModels[0]?.id || flatIds[0]
        }
        settingsStore.setSelectedModel(fallback, 'claude')
      }

      lastLoadedProjectId = pid
    } catch (err) {
      logger.error('useProjectAiModelPicker', 'Failed to load models:', err)
      loadFailed.value = true
    } finally {
      loading.value = false
    }
  }

  async function selectModel(model: AiModelInfo): Promise<void> {
    if (!isModelSelectable(model)) return
    const pid = projectId.value
    if (!pid) return

    settingsStore.setSelectedModel(model.id, model.provider)
    try {
      await api.request('PUT', `/projects/${pid}/ai-settings`, {
        smartModelId: model.id,
        smartModelProvider: model.provider,
      })
    } catch (err) {
      logger.error('useProjectAiModelPicker', 'Failed to save:', err)
    }
  }

  watch(
    projectId,
    (newId) => {
      if (!newId) {
        lastLoadedProjectId = null
        return
      }
      if (newId !== lastLoadedProjectId) {
        loadModels()
      }
    },
    { immediate: true },
  )

  return {
    projectId,
    loading,
    loadFailed,
    allModels,
    selectedModel,
    orderedProviders,
    isModelSelectable,
    loadModels,
    selectModel,
  }
}
